import { useNavigate } from "react-router-dom";
import api, { errMsg, fmtMoney } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Truck, CalendarCheck, Check, MessageSquareWarning } from "lucide-react";

const RESP = { proposed: { label: "Propone nueva fecha", cls: "border-sky-400 bg-sky-50 text-sky-900" },
               rejected: { label: "Rechazó la orden", cls: "border-red-400 bg-red-50 text-red-900" } };

/** Dashboard panel: overdue POs + supplier responses awaiting buyer action. */
export function DeliveriesPanel({ inbox, onChanged }) {
  const navigate = useNavigate();
  const overdue = inbox?.po_overdue || [];
  const responses = inbox?.po_responses || [];
  if (overdue.length === 0 && responses.length === 0) return null;

  const act = async (id, path, okMsg) => {
    try { await api.post(`/purchase-orders/${id}/${path}`); toast.success(okMsg); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="border bg-card p-4 mb-6" data-testid="deliveries-panel">
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1.5 mb-3">
        <Truck size={12} /> Panel de entregas · {overdue.length} vencida(s) · {responses.length} respuesta(s) de proveedor
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-rose-700 mb-2">OCs con entrega vencida</div>
          {overdue.length === 0 ? <div className="text-xs text-muted-foreground italic">Ninguna</div> : (
            <div className="space-y-1.5">
              {overdue.slice(0, 8).map((p) => (
                <button key={p.id} onClick={() => navigate(p.link)} data-testid={`deliveries-overdue-${p.number}`}
                  className="w-full text-left border-l-4 border-rose-500 bg-rose-50 hover:bg-rose-100 p-2 text-xs flex items-center gap-3 transition-colors">
                  <div className="flex-1 min-w-0">
                    <span className="font-black">{p.number}</span> <span className="text-muted-foreground">· {p.supplier}</span>
                    <div className="text-[10px] text-rose-800">Prometida {p.delivery_date} · {p.pending_lines} partida(s) pendiente(s){p.confirmation && <> · {p.confirmation === "confirmed" ? "confirmada" : p.confirmation === "sent" ? "sin confirmar" : p.confirmation}</>}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-black text-rose-700 leading-none tabular-nums">{p.days_overdue}<span className="text-[9px] ml-0.5">d</span></div>
                    <div className="text-[9px] text-muted-foreground">{fmtMoney(p.total)} {p.currency}</div>
                  </div>
                </button>
              ))}
              {overdue.length > 8 && <div className="text-[10px] text-muted-foreground">+{overdue.length - 8} más…</div>}
            </div>
          )}
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-2 flex items-center gap-1"><MessageSquareWarning size={11} /> Respuestas de proveedores por atender</div>
          {responses.length === 0 ? <div className="text-xs text-muted-foreground italic">Ninguna</div> : (
            <div className="space-y-1.5">
              {responses.slice(0, 8).map((p) => {
                const r = RESP[p.status] || RESP.rejected;
                return (
                  <div key={p.id} className={`border-l-4 p-2 text-xs ${r.cls}`} data-testid={`deliveries-response-${p.number}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button className="font-black hover:underline" onClick={() => navigate(p.link)}>{p.number}</button>
                      <span className="text-muted-foreground">· {p.supplier}</span>
                      <span className="ml-auto text-[10px] font-bold uppercase">{r.label}</span>
                    </div>
                    <div className="text-[10px] mt-0.5 opacity-80">
                      {p.status === "proposed" && <>Fecha actual {p.delivery_date || "—"} → propone <b>{p.proposed_delivery_date}</b> · </>}
                      {p.contact_name}{p.comments && <> · "{p.comments}"</>}
                    </div>
                    <div className="flex gap-2 mt-1.5">
                      {p.status === "proposed" && (
                        <Button size="sm" className="rounded-none text-[10px] h-6" onClick={() => act(p.id, "accept-proposed-date", "Fecha aceptada")} data-testid={`deliveries-accept-${p.number}`}>
                          <CalendarCheck size={11} className="mr-1" /> Aceptar fecha
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="rounded-none text-[10px] h-6" onClick={() => act(p.id, "ack-supplier-response", "Marcada como atendida")} data-testid={`deliveries-ack-${p.number}`}>
                        <Check size={11} className="mr-1" /> Atendido
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
